import { SelectLocationSources } from "@/analytics/generic-events-properties";
import { useStoreCameraWithCustomControls } from "@/components/common/view-runtime-context";
import { CameraAnimation } from "@/components/r3f/animations/camera-animation";
import {
  useActiveCamera,
  useDefaultCamera,
} from "@/components/r3f/cameras/active-camera-context";
import { useReduceDprRange } from "@/components/r3f/canvas/adaptive-resolution-canvas";
import { SuspenseLoadNotifier } from "@/components/r3f/utils/suspense-load-notifier";
import { useGoToWaypointEvent } from "@/hooks/use-go-to-waypoint-event";
import { useValidateCacheSize } from "@/object-cache";
import { changeMode } from "@/store/mode-slice";
import {
  selectFlyWalkSceneFilter,
  setFlyWalkSceneFilter,
} from "@/store/modes/flywalk-mode-slice";
import { setActiveElement } from "@/store/selections-slice";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { IS_DEV } from "@/utils/env-constants";
import { parseVector3, selectIElement } from "@faro-lotv/app-component-toolbox";
import { IElementType } from "@faro-lotv/ielement-types";
import { ForwardMovement } from "@faro-lotv/lotv";
import { JSX, RefObject, Suspense, useCallback, useRef, useState } from "react";
import { ModeSceneProps, SceneFilterLookAtInitialState } from "../mode";
import { useCurrentArea, useCurrentScene } from "../mode-data-context";
import { selectWaypointTargetSceneFilter } from "../walk-mode/walk-scene-selectors";
import { InitialAnimationInfo } from "./data-dispatch/flywalk-animation";
import {
  DataDispatchResult,
  FlyWalkDataDispatch,
  INITIAL_DATA_DISPATCH_RESULT,
} from "./data-dispatch/flywalk-data-dispatch";
import { useDowncastObjects } from "./data-dispatch/flywalk-renderables";
import {
  FlyWalkAnimationData,
  INITIAL_FLYWALK_ANIMATION_DATA,
} from "./data-dispatch/use-flywalk-animation";
import { FlyWalkControls } from "./flywalk-controls";
import { FlyWalkWaypoint, useTrackSelectLocation } from "./flywalk-types";
import { FlyWalkRenderers } from "./renderers/flywalk-renderers";
import { FlyWalkRenderingPipeline } from "./rendering-pipeline/flywalk-rendering-pipeline";
import { FlyWalkTools } from "./tools/flywalk-tools";
import { ToolsAcquaintanceContextProvider } from "./tools/tools-acquaintance-context";
import { useFlyWalkModeToolVisibility } from "./tools/use-fly-walk-tools-visibility";
import { useManageCameraControls } from "./use-manage-camera-controls";

/** @returns The main Scene for the Overview Mode */
export function FlyOverviewScene({
  initialState,
}: ModeSceneProps<SceneFilterLookAtInitialState>): JSX.Element {
  const lookAtElement = useAppSelector(
    selectIElement(initialState?.lookAtId ?? ""),
  );
  const canSkipInitialTransition = !!(initialState && initialState.camera);

  const deepLinkTarget = initialState?.camera?.target;

  const initialInfo = useRef<InitialAnimationInfo | null>({
    lookAtElement,
    canSkipInitialTransition,
    target: deepLinkTarget ? parseVector3(deepLinkTarget) : undefined,
  });

  const dispatch = useAppDispatch();

  const store = useAppStore();

  const trackSelectLocation = useTrackSelectLocation();

  const goToWaypoint = useCallback(
    (waypoint: FlyWalkWaypoint) => {
      dispatch(setActiveElement(waypoint.id));
      dispatch(
        setFlyWalkSceneFilter(
          selectWaypointTargetSceneFilter(waypoint)(store.getState()),
        ),
      );
      dispatch(changeMode("walk"));
    },
    [dispatch, store],
  );
  useGoToWaypointEvent(goToWaypoint);

  const onWaypointClicked = useCallback(
    (waypoint: FlyWalkWaypoint) => {
      trackSelectLocation(SelectLocationSources.model3dView);
      goToWaypoint(waypoint);
    },
    [goToWaypoint, trackSelectLocation],
  );

  return (
    <ToolsAcquaintanceContextProvider>
      <FlyOverviewSceneImpl
        initialAnimationInfo={initialInfo}
        onWaypointClicked={onWaypointClicked}
      />
    </ToolsAcquaintanceContextProvider>
  );
}

type FlyOverviewSceneImplProps = {
  /** Information about the initial animation when entering flywalk mode */
  initialAnimationInfo: RefObject<InitialAnimationInfo | null>;
  /** Callback issued when a waypoint is clicked */
  onWaypointClicked(waypoint: FlyWalkWaypoint): void;
};

function FlyOverviewSceneImpl({
  initialAnimationInfo,
  onWaypointClicked,
}: FlyOverviewSceneImplProps): JSX.Element {
  useDefaultCamera();
  const { setCameraProjection } = useActiveCamera();
  useStoreCameraWithCustomControls();

  const flyWalkSceneFilter = useAppSelector(selectFlyWalkSceneFilter);

  const currentScene = useCurrentScene();
  const currentArea = useCurrentArea();

  const [dataDispatchResult, setDataDispatchResult] =
    useState<DataDispatchResult>(INITIAL_DATA_DISPATCH_RESULT);

  const [animationData, setAnimationData] = useState<FlyWalkAnimationData>(
    INITIAL_FLYWALK_ANIMATION_DATA,
  );

  // 'dataDispatchResult' contains the project data that should be dispatched to this scene for rendering,
  // according to the user selection and the scene filter
  const {
    main,
    mainObject,
    overlayObject,
    sheetObjects,
    annotations,
    measurements,
    imageType,
  } = dataDispatchResult;

  // 'animationData' contains the current animation state and the previous main object for rendering.
  // Notably, the 'currentSceneFilter' below is different from the 'flyWalkSceneFilter' above.
  // The latter refers to what the user has selected in the UI. The former refers to what the
  // rendering pipeline is ready to take as input (as opposed to an animation)
  const {
    flyWalkAnimation,
    currentSceneFilter,
    previousMainObject,
    target,
    setTarget,
    endAnimation,
    setCameraView,
  } = animationData;

  // Retrieves the current renderable objects with their correct types.
  const renderables = useDowncastObjects(
    mainObject,
    overlayObject,
    previousMainObject,
    currentSceneFilter,
    currentScene.activeSheets,
  );

  // Adds logic for camera predefined views, camera projection switch, etc.
  const setControls = useManageCameraControls(
    renderables,
    setCameraView,
    setCameraProjection,
  );

  useReduceDprRange();

  useFlyWalkModeToolVisibility(main ?? undefined, currentSceneFilter);

  return (
    <>
      <Suspense
        fallback={<SuspenseLoadNotifier withSnapshotRenderer={false} />}
      >
        <FlyWalkDataDispatch
          flyWalkSceneFilter={flyWalkSceneFilter}
          currentScene={currentScene}
          currentArea={currentArea}
          initialAnimationInfo={initialAnimationInfo}
          setDataDispatchResult={setDataDispatchResult}
          setAnimationData={setAnimationData}
        />
      </Suspense>
      <FlyWalkRenderers
        renderables={renderables}
        sheets={sheetObjects}
        panos={currentScene.panos}
        paths={currentScene.paths}
        sheetForElevation={currentScene.activeSheetFor2DNavigation}
        annotations={annotations}
        measurements={measurements}
        animation={flyWalkAnimation?.renderingAnimation ?? null}
        sceneFilter={currentSceneFilter}
        imageType={imageType}
        isOverview
        onPanoClicked={onWaypointClicked}
      />
      {!flyWalkAnimation?.cameraAnimation && (
        <FlyWalkControls
          imageType={imageType}
          animation={flyWalkAnimation}
          target={target}
          setTarget={setTarget}
          forwardMovement={ForwardMovement.Fly}
          clickableByControls={renderables.clickableByControls}
          smartNavigationAvailable
          setControls={setControls}
        />
      )}
      <FlyWalkRenderingPipeline
        pointCloud={renderables.pointCloud}
        cadModel={renderables.cadModel}
        sceneFilter={currentSceneFilter}
        animation={flyWalkAnimation?.renderingAnimation ?? null}
        endAnimation={endAnimation}
      />
      <FlyWalkTools
        sceneFilter={currentSceneFilter}
        renderables={renderables}
        isAnimating={!!flyWalkAnimation}
        imageType={imageType}
        paths={currentScene.paths}
      />
      {flyWalkAnimation?.cameraAnimation && (
        <CameraAnimation
          {...flyWalkAnimation.cameraAnimation}
          onAnimationFinished={endAnimation}
        />
      )}
      {IS_DEV && <ObjectCacheValidator />}
    </>
  );
}

/**
 * This component validates that, in overview mode, no more than the allowed number of objects per type
 * are present in the object cache.
 *
 * @returns null
 */
function ObjectCacheValidator(): null {
  useValidateCacheSize(IElementType.pointCloudStream, 2);
  useValidateCacheSize(IElementType.pointCloudStreamWebShare, 2);
  useValidateCacheSize(IElementType.model3dStream, 1);
  useValidateCacheSize(IElementType.img360, 0);
  useValidateCacheSize(IElementType.img2d, 0);
  useValidateCacheSize(IElementType.gaussianSplats, 2);
  return null;
}
